import {
  Callout,
  Code,
  DocPage,
  DocSection,
  K,
  P,
  PageFooter,
  PromptCard,
  UL,
} from "./_components";

export function Troubleshooting() {
  return (
    <DocPage
      eyebrow="Troubleshooting"
      title="When something doesn't look right"
      intro={
        <>
          Every confusion non-engineers actually hit in their first hour. Most
          fixes are a single prompt or a one-tab navigation, not a code change.
        </>
      }
    >
      <DocSection id="missing-widget" title="I built a widget but my project page is empty">
        <P>
          The most common gotcha. <K>dynamic_widget</K> defaults to the{" "}
          <strong>Inbox</strong> (a 24h ephemeral scratch space), not a named
          project. So if you asked for "an Orders project with a widget for X",
          the project got created — but the widget went to <K>/projects/__inbox</K>,
          not <K>/projects/orders</K>.
        </P>
        <P>Two fixes:</P>
        <UL>
          <li>
            <strong>In your original prompt</strong>, tell the AI to set{" "}
            <K>projectName: "Orders"</K> on the <K>dynamic_widget</K> call. It
            auto-saves into Orders in the same step.
          </li>
          <li>
            <strong>If it's already in Inbox</strong>, open the Inbox, click
            the widget, hit <K>Save to project</K>. Or in chat:
          </li>
        </UL>
        <PromptCard
          title="Save the most recent Inbox widget into a project"
          prompt={`Use list_widgets to find my most recent inbox widget, then save_widget_to_app it into my "Orders" project. Keep the tool slug "orders-table".`}
        />
      </DocSection>

      <DocSection id="blank-iframe" title="The widget renders in Claude but my web preview is blank">
        <P>
          We added an inline error surface — broken widgets now print the
          actual JavaScript stack in the iframe rather than going blank. Open
          the project page, click the widget, look in the preview pane: you'll
          see something like
        </P>
        <Code>{`Widget error
TypeError: Cannot read properties of undefined (reading 'map')
  at Widget (orders-table:23:18)`}</Code>
        <P>Recovery is one prompt — copy the stack into your chat and ask:</P>
        <PromptCard
          title="One-shot remix to fix the error"
          prompt={`The orders-table widget on my "Orders" project is throwing this in the iframe:

  TypeError: Cannot read properties of undefined (reading 'map')
    at Widget (orders-table:23:18)

Use remix_widget on it. Default rows to [] so it renders an empty state instead of crashing. Keep everything else the same.`}
        />
      </DocSection>

      <DocSection id="oauth" title="Claude says “Authorization with the MCP server failed” / ofid_…">
        <P>
          The OAuth handshake has always worked when the URL is right, so an{" "}
          <K>ofid_…</K> error almost always means the URL has an invisible
          typo. Things to check:
        </P>
        <UL>
          <li>
            <strong>Trailing punctuation</strong> — a stray backtick or quote
            copied from a markdown code block. Use{" "}
            <K>https://mcpbuild.app/mcp</K> raw — no quotes, no backticks.
          </li>
          <li>
            <strong>Trailing slash</strong> — both <K>/mcp</K> and{" "}
            <K>/mcp/</K> work; nothing else after.
          </li>
          <li>
            <strong>Project URL typo</strong> — <K>/mcp/orders</K>, not{" "}
            <K>/mcp/Orders/</K>. Project slugs are kebab-case-lowercase.
          </li>
          <li>
            <strong>Stale connector</strong> — remove the connector from your
            chat client and re-add it. Tokens get cached.
          </li>
        </UL>
        <Callout kind="note" title="Stuck after that?">
          We log every OAuth handshake — give us the rough timestamp (and the{" "}
          <K>ofid_…</K> reference) on Discord and we can pull the trace.
        </Callout>
      </DocSection>

      <DocSection id="natural" title="My audience said “show me orders” and nothing happened">
        <P>
          The model didn't route their phrase to your tool. Two likely causes:
        </P>
        <UL>
          <li>
            <strong>The phrase isn't in the trigger list.</strong> Add it.
            Three to five paraphrases of the same intent usually does it.
          </li>
          <li>
            <strong>The tool's description is too vague</strong> ("a tool for
            orders"). The description is the model's primary router — make it
            specific (<K>"Show open orders for a given week with status and
            customer."</K>).
          </li>
        </UL>
        <PromptCard
          title="Boost the description + add phrases in one shot"
          prompt={`Use project configure_tool on my "Orders" project, tool "orders-table". Update toolDescription to "Render the current week's orders as a table with status and customer. Use when the user asks to see open orders, the order list, or this week's pipeline." Set triggerPhrases ["show me orders", "what orders are open", "what's the order list looking like", "this week's pipeline"].`}
        />
      </DocSection>

      <DocSection id="fetchData" title="“fetch not available” or “domain not allowed”">
        <P>Two flavors of the same problem:</P>
        <UL>
          <li>
            <strong>Widget is in Inbox.</strong> The Inbox has no project to
            host allowlists, so <K>fetchData</K> fails. Save the widget into a
            real project (<K>projectName</K>).
          </li>
          <li>
            <strong>Domain isn't allowlisted.</strong> Add it via{" "}
            <K>project set_fetch_allowlist</K>. Wildcards (
            <K>*.example.com</K>) work.
          </li>
        </UL>
      </DocSection>

      <DocSection id="versions" title="I broke a widget — can I roll back?">
        <P>
          Yes. Every <K>remix_widget</K> saves a new version and keeps the old
          ones. Open the tool's <K>Versions</K> tab and click Restore on any
          row. Or in chat:
        </P>
        <PromptCard
          title="Roll back to a working version"
          prompt={`Use widget versions on my "Orders" project, tool "orders-table" — list them. Then use widget restore to go back to version <pick-one>.`}
        />
      </DocSection>

      <DocSection id="contact" title="Still stuck?">
        <P>
          Most of the early confusions on MCPBuild have been threaded out on
          Discord — drop a message with a screenshot or the error text and
          we'll either fix it on the spot or tell you how. Read the other
          guides first, then ask.
        </P>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/remixing", label: "Remixing & forking" }}
      />
    </DocPage>
  );
}
